import { capSQLiteSet } from "@capacitor-community/sqlite";
import { store } from "@stencil/redux";
import { appUpdateSyncState } from "../store/app/app.actions";
import { ApplicationState } from "../store/reducers";
import { getAppId, getAppNetworkConnected, getAuthIsAuth } from "../store/selectors";
import AppDatabase from "./db/app-db.service";
import SQLDatabase, { TransactionQueryInterface } from "./db/sql.service";

const PROJECT_POLLING_INTERVAL = 60000;
const PROJECT_POLLING_TIMEOUT = 10000;

export default class PollingService {

  private timer: number;
  private pollTimer: number;
  private abortController: AbortController;
  private syncIsActive: boolean = false;

  public authenticated: boolean = false;
  public projectId: number = null;
  public appUpdateSyncState: typeof appUpdateSyncState;
  public networkConnected: boolean = false;

  public static instance: PollingService = new PollingService();

  private constructor() { }

  private async doPoll() {
    if (!(this.projectId && this.authenticated) || this.syncIsActive) {
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    if ('AbortController' in window) {
      this.abortController = new AbortController();
      this.pollTimer = window.setTimeout(() => {
        this.abortController?.abort();
      }, PROJECT_POLLING_TIMEOUT);
    }

    const query: TransactionQueryInterface = (set: capSQLiteSet) => SQLDatabase.instance.querySet(set);

    // Store projectId here in case it changes during async check
    const projectId = this.projectId;

    const result = await AppDatabase.instance.syncCheck(projectId, this.abortController?.signal, query);
    this.abortController = null;

    if (this.pollTimer) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    // Only dispatch an action if something changed
    if (result && (result.project > 0 || result.records > 0)) {
      this.appUpdateSyncState(projectId, result);
    }
  }

  // Setup listeners
  init() {

    AppDatabase.instance.events.addListener('syncStart', () => {
      this.abortController?.abort();
      this.syncIsActive = true;
    });

    AppDatabase.instance.events.addListener('syncComplete', () => {
      this.syncIsActive = false;
    });

    AppDatabase.instance.events.addListener('syncError', () => {
      this.syncIsActive = false;
    });

    store.mapStateToProps(this, (state: ApplicationState) => {
      const networkIsNowConnected = getAppNetworkConnected(state);
      if (!this.networkConnected && networkIsNowConnected) {
        this.run();
      } else if (this.networkConnected && !networkIsNowConnected) {
        this.clear();
      }

      return {
        authenticated: getAuthIsAuth(state),
        projectId: getAppId(state),
        networkConnected: networkIsNowConnected
      };
    });
    store.mapDispatchToProps(this, { appUpdateSyncState });
  }

  async run() {
    if (this.timer) {
      this.clear();
    }

    try {
      await this.doPoll();
      this.timer = null;
    } catch (e) {
      console.warn(e);
    }
    this.timer = window.setTimeout(() => this.run(), PROJECT_POLLING_INTERVAL);
  }

  clear() {
    window.clearTimeout(this.timer);
  }

}
